import React, { useEffect, useMemo, useRef } from "react";
import ButtonsHeader from "../../components/common/ButtonsHeader";
import HelpQueriesSkeleton, {
  HelpQueryCardSkeleton,
} from "@/app/components/common/skeletons/HelpQueriesSkeleton";
import {
  defaultPaginationOptions,
  endpoints,
  formatDate,
  ISupport,
  SupportStatus,
} from "muminsmart-utils";
import { inter, philosopher } from "@/lib/fonts";
import { useRtkInfiniteList } from "@/lib/hooks/useRtkInfiniteList";

function statusColor(status: SupportStatus) {
  switch (status) {
    case SupportStatus.Resolved:
      return "text-green-500";
    case SupportStatus.InProgress:
      return "text-blue-500";
    case SupportStatus.Closed:
      return "text-gray-500";
    default:
      return "text-amber-500";
  }
}

export default function PastQueries() {
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const baseParams = useMemo(
    () => ({
      ...defaultPaginationOptions,
      sortBy: "createdAt",
      sortOrder: "DESC",
    }),
    [],
  );

  const {
    items: requests,
    page,
    hasMore,
    isInitialLoading,
    isLoadingMore,
    isLoading,
    loadPage,
  } = useRtkInfiniteList<ISupport>({
    endpoint: endpoints.support.myRequests,
    baseParams,
    errorMessage: "Failed to fetch service requests",
  });

  useEffect(() => {
    void loadPage(1, true);
  }, [loadPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoading) {
          void loadPage(page + 1);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, isLoading, loadPage, page]);

  return (
    <div className="min-h-screen">
      <ButtonsHeader heading="Help Desk" showButtons={false} />

      <div className="mt-6 min-h-screen rounded-t-3xl bg-white px-5 pb-10 pt-8">
        <h2
          className={`mb-5 text-lg font-bold text-[#0D4A5A] ${philosopher.className}`}
        >
          Past Queries
        </h2>

        {isInitialLoading ? (
          <HelpQueriesSkeleton />
        ) : requests.length === 0 ? (
          <p
            className={`py-16 text-center text-sm text-gray-400 ${inter.className}`}
          >
            No queries raised yet.
          </p>
        ) : (
          <div className="space-y-3">
            {requests.map((req) => (
              <div
                key={req.id}
                className="space-y-1 rounded-3xl border border-muted p-3"
              >
                <p className={`text-xs text-gray-400 ${inter.className}`}>
                  {formatDate(req.createdAt)}
                </p>
                <h3
                  className={`text-sm font-semibold text-[#1A1A1A] ${inter.className}`}
                >
                  {req.name}
                </h3>
                <p
                  className={`wrap-break-word text-xs text-gray-500 ${inter.className}`}
                >
                  {req.message}
                </p>
                <p
                  className={`text-xs font-semibold ${statusColor(req.status)} ${inter.className}`}
                >
                  &#8226; {req.status}
                </p>
              </div>
            ))}
          </div>
        )}

        {isLoadingMore && !isInitialLoading ? <HelpQueryCardSkeleton /> : null}
        <div ref={sentinelRef} className="h-1 w-full" aria-hidden="true" />
      </div>
    </div>
  );
}
